import type { PropsWithChildren, ReactNode } from 'react';
import { Children } from 'react';
import { Icon, Typography, space, tok, tokPx } from '@sunwebgroup/ui-components';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { styled } from 'styled-components';
import { HeaderLink } from '../HeaderLink';
import { MenuTrigger } from './Trigger';
import { SubMenu, type SubMenuProps } from './SubMenu';

export function NestedSubMenu({ children, label }: PropsWithChildren<SubMenuProps>) {
  return (
    <>
      <MenuTrigger onPointerMove={(e: React.PointerEvent) => e.preventDefault()}>
        {label}
        <Icon glyph="IconChevronRightSmall" size="md" />
      </MenuTrigger>
      <NavigationMenu.Content forceMount>
        <Typography as="h3" $variant="body-md-bold">
          {label}
        </Typography>
        <StyledNestedSubMenu orientation="vertical">
          <NestedMenuList>{wrapChildrenWithItem(children)}</NestedMenuList>
          <NavigationMenu.Viewport />
        </StyledNestedSubMenu>
      </NavigationMenu.Content>
    </>
  );
}

const StyledNestedSubMenu = styled(NavigationMenu.Sub)`
  & > * {
    width: 100%;
  }
`;

const NestedMenuList = styled(SubMenu.List)`
  align-content: flex-start;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-height: 500px;
  @media (min-width: ${tokPx('primitives_breakpoint_l_min')}) {
    max-height: 250px;
  }

  ${SubMenu.Item} {
    min-width: 200px;
    padding-right: ${space('primitives_space_32')};

    ${HeaderLink}:hover {
      background: none;
      color: ${tok('semantics_links_hover_color')} !important;
    }
  }
`;

function wrapChildrenWithItem(children: ReactNode | ReactNode[]) {
  return Children.map(children, (child, index) => <SubMenu.Item key={index}>{child}</SubMenu.Item>);
}
